export interface ValidationRule {
  validate: (value: string) => boolean;
  errorText: string;
}

export type ValidationConfig = Record<string, ValidationRule[]>;

/**
 * Rule chạy theo thứ tự khai báo, rule đầu tiên fail thì thắng — nên "bắt buộc"
 * luôn đứng trước kiểm tra định dạng, giống mẫu validationConfig của Cloudscape.
 */
export function validateField(config: ValidationConfig, field: string, value: string): string | undefined {
  for (const rule of config[field] || []) {
    if (!rule.validate(value)) return rule.errorText;
  }
  return undefined;
}

export function validateAll(config: ValidationConfig, values: Record<string, string>): Record<string, string> {
  const errors: Record<string, string> = {};
  for (const field of Object.keys(config)) {
    const error = validateField(config, field, values[field] ?? "");
    if (error) errors[field] = error;
  }
  return errors;
}

export const notEmpty = (value: string): boolean => value.trim().length > 0;

// Cố ý lỏng: RFC 5322 đầy đủ không đáng, backend mới là nơi chốt.
export const isEmail = (value: string): boolean => /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(value.trim());

export const maxLength =
  (limit: number) =>
  (value: string): boolean =>
    value.length <= limit;

export const PASSWORD_MIN_LENGTH = 6;
export const PASSWORD_MAX_LENGTH = 128;

// Chỉ đo độ dài, cố ý không đòi chữ hoa/số/ký tự đặc biệt — phải khớp với
// validate_password_strength ở backend/app/routers/public_auth.py.
export const isValidPassword = (value: string): boolean =>
  value.length >= PASSWORD_MIN_LENGTH && value.length <= PASSWORD_MAX_LENGTH;

/** Đặt dưới field mật khẩu — người dùng phải biết luật trước khi nhập sai. */
export const PASSWORD_CONSTRAINT = `Mật khẩu cần ${PASSWORD_MIN_LENGTH} đến ${PASSWORD_MAX_LENGTH} ký tự.`;

export const EMAIL_CONSTRAINT = "Nhập email hợp lệ. Ví dụ: ban@example.com";

export const emailRules: ValidationRule[] = [
  { validate: notEmpty, errorText: "Email là bắt buộc." },
  { validate: isEmail, errorText: "Nhập email hợp lệ." },
  { validate: maxLength(320), errorText: "Email quá dài. Tối đa 320 ký tự." },
];

export const passwordRules: ValidationRule[] = [
  { validate: notEmpty, errorText: "Mật khẩu là bắt buộc." },
  { validate: isValidPassword, errorText: PASSWORD_CONSTRAINT },
];

export const nameRules: ValidationRule[] = [
  { validate: notEmpty, errorText: "Họ tên là bắt buộc." },
  { validate: maxLength(160), errorText: "Họ tên quá dài. Tối đa 160 ký tự." },
];

export function confirmationRules(getPassword: () => string): ValidationRule[] {
  return [
    { validate: notEmpty, errorText: "Nhập lại mật khẩu là bắt buộc." },
    { validate: (value) => value === getPassword(), errorText: "Mật khẩu nhập lại chưa khớp." },
  ];
}

/** Đủ để focus lỗi; Input/Select/Textarea của Cloudscape đều thoả. */
export interface Focusable {
  focus: () => void;
}

export type FocusableRef = { current: Focusable | null };

/**
 * Thứ tự key của `refs` chính là thứ tự hiển thị trên trang — đó là điều khiến
 * "lỗi trên cùng" đúng nghĩa. Gọi sau khi submit thất bại.
 */
export function focusTopMostError(
  refs: Record<string, FocusableRef>,
  errors: Record<string, string | undefined>,
): void {
  for (const [field, ref] of Object.entries(refs)) {
    if (errors[field] && ref.current) {
      ref.current.focus();
      return;
    }
  }
}
